import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api, type SupportAdminList, type SupportKind } from "@/api/client";
import { useEscClose } from "@/lib/escClose";
import {
  adminRowSub,
  fmtTicketDate,
  groupByCompany,
  groupByUser,
  initials,
  isFlat,
  kindLabel,
  modeButtonTitle,
  nextMode,
  nextStatus,
  statusButtonTitle,
  SUPPORT_KINDS,
  type AdminGroup,
  type AdminMode,
  type AdminStatus,
} from "./supportLogic";

// Левая колонка панели администратора: «Обращения · Администратор», поиск,
// кнопка статуса и кнопка режима (обе по кругу), фильтры, чипы и список —
// плоский или группами (эталон: overlays/Support, режим admin).

const SEARCH_DELAY_MS = 250;

const Svg = (props: { children: ReactNode }) => (
  <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {props.children}
  </svg>
);

function StatusGlyph({ status, need }: { status: AdminStatus; need: number }) {
  if (status === "need") return <>{need}</>;
  if (status === "wait") {
    return (
      <Svg>
        <circle cx="10" cy="10" r="6.5" />
        <path d="M10 6.4V10l2.4 1.6" />
      </Svg>
    );
  }
  if (status === "closed") {
    return (
      <Svg>
        <circle cx="10" cy="10" r="6.5" />
        <path d="M7 10.2l2.2 2.2L13.2 8" />
      </Svg>
    );
  }
  return (
    <Svg>
      <path d="M4 6h12M4 10h12M4 14h12" />
    </Svg>
  );
}

function ModeGlyph({ mode }: { mode: AdminMode }) {
  if (mode === "tickets") {
    return (
      <Svg>
        <path d="M3.5 4.5h13v8.5H9l-3.5 3v-3h-2z" />
      </Svg>
    );
  }
  if (mode === "users") {
    return (
      <Svg>
        <circle cx="10" cy="7" r="3" />
        <path d="M4 16.5c0-3 2.6-4.6 6-4.6s6 1.6 6 4.6" />
      </Svg>
    );
  }
  return (
    <Svg>
      <path d="M4.5 17V4.5h7V17M11.5 8.5h4V17M2.5 17h15M7 7.5h2M7 10.5h2M7 13.5h2" />
    </Svg>
  );
}

export function AdminList({
  selectedId,
  reloadKey,
  onSelect,
  onExit,
}: {
  selectedId: string | null;
  /** Меняется, когда в переписке что-то случилось: список читается заново. */
  reloadKey: number;
  onSelect: (id: string) => void;
  /** Закрыть окно: в узком окне, где виден только список, крестика переписки нет. */
  onExit: () => void;
}) {
  const [status, setStatus] = useState<AdminStatus>("need");
  const [mode, setMode] = useState<AdminMode>("tickets");
  const [kind, setKind] = useState<SupportKind | null>(null);
  const [company, setCompany] = useState<string | null>(null);
  const [person, setPerson] = useState<{ id: string; name: string } | null>(null);
  const [q, setQ] = useState("");
  const [dq, setDq] = useState("");
  const [panel, setPanel] = useState(false);
  const [data, setData] = useState<SupportAdminList | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const t = window.setTimeout(() => setDq(q), SEARCH_DELAY_MS);
    return () => window.clearTimeout(t);
  }, [q]);

  useEscClose(() => setPanel(false), panel);

  useEffect(() => {
    const ctl = new AbortController();
    api
      .supportAdminList({ status, kind, company, user_id: person?.id ?? null, q: dq }, ctl.signal)
      .then((d) => {
        setData(d);
        setFailed(false);
      })
      .catch(() => {
        if (!ctl.signal.aborted) setFailed(true);
      });
    return () => ctl.abort();
  }, [status, kind, company, person?.id, dq, reloadKey]);

  const companies = data?.companies ?? [];
  // Компания в базе одна — группировать и фильтровать по ней нечего.
  const hasCompanies = companies.length > 1;
  const items = data?.items ?? [];
  const counts = data?.counts;
  const flat = isFlat(mode, person?.id ?? null, company);
  const groups: AdminGroup[] = useMemo(() => {
    if (flat) return [];
    return mode === "users" ? groupByUser(items) : groupByCompany(items);
  }, [flat, mode, items]);

  const chips: { label: string; clear: () => void }[] = [];
  if (company) chips.push({ label: `Компания · ${company}`, clear: () => setCompany(null) });
  if (person) chips.push({ label: `Пользователь · ${person.name}`, clear: () => setPerson(null) });
  if (kind) chips.push({ label: `Тип · ${kindLabel(kind)}`, clear: () => setKind(null) });

  const statusCls =
    status === "need" ? " sup-sq--need" : status === "wait" ? " sup-sq--wait" : status === "closed" ? " sup-sq--closed" : "";
  const empty = data !== null && (flat ? !items.length : !groups.length);

  return (
    <div className="sup-list">
      <div className="sup-list-head">
        <b className="sup-title">Обращения</b>
        <span className="sup-badge">Администратор</span>
        <span className="sup-grow" />
        <button type="button" className="sup-x sup-list-x" title="Закрыть" aria-label="Закрыть" onClick={onExit}>
          <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </button>
      </div>
      <div className="sup-bar">
        <label className="sup-search">
          <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="9" cy="9" r="5.5" />
            <path d="M13.5 13.5l4 4" strokeLinecap="round" />
          </svg>
          <input placeholder="Найти" value={q} onChange={(e) => setQ(e.target.value)} />
        </label>
        <button
          type="button"
          className={"sup-sq" + statusCls}
          title={statusButtonTitle(status, counts ? counts[status] : 0)}
          aria-label={statusButtonTitle(status, counts ? counts[status] : 0)}
          onClick={() => setStatus(nextStatus(status))}
        >
          <StatusGlyph status={status} need={counts?.need ?? 0} />
        </button>
        <button
          type="button"
          className={"sup-sq" + (mode !== "tickets" ? " sup-sq--on" : "")}
          title={modeButtonTitle(mode, hasCompanies)}
          aria-label={modeButtonTitle(mode, hasCompanies)}
          onClick={() => setMode(nextMode(mode, hasCompanies))}
        >
          <ModeGlyph mode={mode} />
        </button>
        <button
          type="button"
          className={"sup-sq" + (panel ? " sup-sq--on" : "")}
          title="Фильтры"
          aria-label="Фильтры"
          aria-expanded={panel}
          onClick={() => setPanel((p) => !p)}
        >
          <Svg>
            <path d="M3 7h6.6M14.4 7H17M3 13h2.6M10.4 13H17" />
            <circle cx="12" cy="7" r="2.4" />
            <circle cx="8" cy="13" r="2.4" />
          </Svg>
          {(kind || company) && <i />}
        </button>
      </div>
      <div className="sup-fw">
        {panel && (
          <>
            <div className="sup-fov" onClick={() => setPanel(false)} />
            <div className="sup-fp">
              <div className="sup-fcap">Тип обращения</div>
              {[{ value: null, label: "Все типы" }, ...SUPPORT_KINDS].map((k) => (
                <button
                  key={k.label}
                  type="button"
                  className={"sup-radio" + (kind === k.value ? " sup-radio--on" : "")}
                  onClick={() => setKind(k.value)}
                >
                  <i />
                  <span>{k.label}</span>
                </button>
              ))}
              {hasCompanies && (
                <>
                  <div className="sup-fcap">Компания</div>
                  <CompanyField
                    companies={companies}
                    value={company}
                    onPick={(c) => {
                      setCompany(c);
                      // Выбранный человек из другой компании остался бы в чипах и
                      // обнулял выдачу — как в эталоне, сбрасываем.
                      setPerson(null);
                    }}
                  />
                </>
              )}
            </div>
          </>
        )}
      </div>
      {chips.length > 0 && (
        <div className="sup-chips">
          {chips.map((c) => (
            <span className="sup-chip" key={c.label}>
              {c.label}
              <button type="button" aria-label="Убрать фильтр" onClick={c.clear}>
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="sup-scroll">
        {flat &&
          items.map((r) => (
            <button
              key={r.id}
              type="button"
              className={"sup-item" + (selectedId === r.id ? " sup-item--on" : "")}
              onClick={() => onSelect(r.id)}
            >
              <span className="sup-item-top">
                <span className="sup-item-name">{r.title}</span>
                <span className="sup-item-time">{fmtTicketDate(r.last_message_at)}</span>
              </span>
              <span className="sup-item-prev">
                <span className="sup-item-sub">{adminRowSub(r, hasCompanies)}</span>
                {r.status === "need" && <span className="sup-need">Ждёт ответа</span>}
                {r.status === "closed" && <span className="sup-done">Закрыто</span>}
              </span>
            </button>
          ))}
        {!flat &&
          groups.map((g) => (
            <button
              key={g.key}
              type="button"
              className="sup-group"
              onClick={() => {
                if (g.isCompany) setCompany(g.key === "—" ? null : g.key);
                else setPerson({ id: g.key, name: g.name });
              }}
            >
              <span className={"sup-av" + (g.isCompany ? " sup-av--co" : "")}>{initials(g.name)}</span>
              <span className="sup-group-main">
                <span className="sup-group-name">{g.name}</span>
                <span className="sup-group-sub">{g.sub}</span>
              </span>
              {g.need > 0 && <span className="sup-need">Ждут ответа · {g.need}</span>}
              <span className="sup-group-n">{g.count}</span>
            </button>
          ))}
        {empty && (
          <div className="sup-empty">
            <b>Ничего не найдено</b>
          </div>
        )}
        {!data && !failed && <div className="sup-empty">Загрузка…</div>}
        {failed && !data && (
          <div className="sup-empty">
            <b>Не удалось загрузить обращения</b>
          </div>
        )}
      </div>
    </div>
  );
}

/** Поле «Все компании» с поиском по списку (эталон: .ozf). */
function CompanyField({
  companies,
  value,
  onPick,
}: {
  companies: { name: string; count: number }[];
  value: string | null;
  onPick: (c: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const needle = open ? text.trim().toLowerCase() : "";
  const list = companies.filter((c) => !needle || c.name.toLowerCase().includes(needle));
  return (
    <div className="sup-co">
      <div className="sup-co-fld">
        <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="9" cy="9" r="5.5" />
          <path d="M13.5 13.5l4 4" strokeLinecap="round" />
        </svg>
        <input
          placeholder={value ?? "Все компании"}
          value={open ? text : (value ?? "")}
          onFocus={() => {
            setOpen(true);
            setText("");
          }}
          onChange={(e) => {
            setText(e.target.value);
            setOpen(true);
          }}
          onBlur={() => {
            setOpen(false);
            setText("");
          }}
        />
      </div>
      {open && (
        // mousedown не отдаёт фокус полю: иначе blur закрыл бы список раньше щелчка.
        <div className="sup-co-dd" onMouseDown={(e) => e.preventDefault()}>
          <button
            type="button"
            className="sup-co-all"
            onClick={() => {
              onPick(null);
              setOpen(false);
            }}
          >
            Все компании
          </button>
          <div className="sup-co-list">
            {list.map((c) => (
              <button
                key={c.name}
                type="button"
                className={"sup-co-row" + (value === c.name ? " sup-co-row--on" : "")}
                onClick={() => {
                  onPick(c.name);
                  setOpen(false);
                  setText("");
                }}
              >
                <span>{c.name}</span>
                <span>{c.count}</span>
              </button>
            ))}
            {!list.length && (
              <div className="sup-co-row">
                <span>Не найдено</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
